import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Help component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconHelp extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.10547 12C1.10547 5.98284 5.98333 1.10498 12.0005 1.10498C18.0177 1.10498 22.8955 5.98284 22.8955 12C22.8955 18.0172 18.0177 22.895 12.0005 22.895C5.98333 22.895 1.10547 18.0172 1.10547 12ZM12.0005 2.60498C6.81176 2.60498 2.60547 6.81127 2.60547 12C2.60547 17.1888 6.81175 21.395 12.0005 21.395C17.1893 21.395 21.3955 17.1888 21.3955 12C21.3955 6.81126 17.1893 2.60498 12.0005 2.60498ZM12.0004 7.30176C10.9219 7.30176 10.0408 8.18182 10.0408 9.27588C10.0408 9.69009 9.70498 10.0259 9.29077 10.0259C8.87656 10.0259 8.54077 9.69009 8.54077 9.27588C8.54077 7.36096 10.0859 5.80176 12.0004 5.80176C13.9148 5.80176 15.46 7.36096 15.46 9.27588C15.46 10.9331 14.3027 12.3239 12.7504 12.6682V13.8125C12.7504 14.2267 12.4146 14.5625 12.0004 14.5625C11.5862 14.5625 11.2504 14.2267 11.2504 13.8125V12C11.2504 11.5858 11.5862 11.25 12.0004 11.25C13.0788 11.25 13.96 10.3699 13.96 9.27588C13.96 8.18182 13.0788 7.30176 12.0004 7.30176ZM12.0115 18.207C12.6738 18.207 13.2107 17.6701 13.2107 17.0078C13.2107 16.3455 12.6738 15.8086 12.0115 15.8086C11.3492 15.8086 10.8123 16.3455 10.8123 17.0078C10.8123 17.6701 11.3492 18.207 12.0115 18.207Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-help') === undefined) {
  customElements.define('cat-icon-help', BlocksIconHelp);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-help': BlocksIconHelp;
  }
}
